import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, LogOut, User, AlertCircle, Check, X, ChevronLeft, ChevronRight, Wifi, WifiOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { readJson } from '../lib/fetchHelpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');

// Norwegian month names
const MONTHS_NO = ['Januar', 'Februar', 'Mars', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Desember'];
const DAYS_NO = ['Søn', 'Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør'];

// English month names
const MONTHS_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS_EN = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const LOCALE_MAP = { no: 'nb-NO', en: 'en-GB', so: 'so-SO', ar: 'ar', fr: 'fr-FR', es: 'es-ES' };


const toLocalKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const TennisBooking = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [myBookings, setMyBookings] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null); // currently picked slot (not yet added to cart)
  const [duration, setDuration] = useState(1);
  const [pendingBookings, setPendingBookings] = useState([]); // cart of slots ready to be confirmed
  const [loading, setLoading] = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  const lang = (i18n.language || 'no').split('-')[0];
  const locale = LOCALE_MAP[lang] || 'nb-NO';
  const months = lang === 'no' ? MONTHS_NO : lang === 'en' ? MONTHS_EN : Array.from({ length: 12 }, (_, m) => new Date(2024, m, 1).toLocaleDateString(locale, { month: 'long' }));
  const days = lang === 'no' ? DAYS_NO : lang === 'en' ? DAYS_EN : Array.from({ length: 7 }, (_, d) => new Date(2024, 0, 7 + d).toLocaleDateString(locale, { weekday: 'short' }));

  // WebSocket connection for real-time updates
  const connectWebSocket = useCallback((date) => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close();
    }

    const ws = new WebSocket(`${WS_URL}/api/ws/tennis/availability/${date}`);
    
    ws.onopen = () => {
      setWsConnected(true);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.date && data.date !== date) return;
        if (data.type === 'initial_data' || data.type === 'availability_update') {
          setAvailableSlots(Array.isArray(data.slots) ? data.slots : []);
          setSlotsLoading(false);
        }
      } catch (e) {
        // Handle ping/pong text messages
        if (event.data === 'ping') {
          ws.send('pong');
        }
      }
    };

    ws.onclose = () => {
      setWsConnected(false);
      if (wsRef.current !== ws) return;
      // Try to reconnect after 3 seconds to the same date
      reconnectTimeoutRef.current = setTimeout(() => {
        if (wsRef.current === ws) connectWebSocket(date);
      }, 3000);
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      setWsConnected(false);
    };

    wsRef.current = ws;
  }, []);

  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, []);

  // Check authentication
  useEffect(() => {
    const token = localStorage.getItem('tennis_token');
    const memberData = localStorage.getItem('tennis_member');
    
    if (!token || !memberData) {
      navigate('/tennis/login');
      return;
    }

    const parsedMember = JSON.parse(memberData);
    
    if (!parsedMember.is_approved) {
      toast.error(t('tennis.pendingApproval'));
      navigate('/tennis/login');
      return;
    }

    setMember(parsedMember);
    fetchMyBookings(token);
    setLoading(false);
  }, [navigate, t]);

  const fetchMyBookings = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/tennis/bookings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await readJson(response);
        setMyBookings(data);
      }
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    }
  };

  const fetchAvailableSlots = async (date) => {
    setSlotsLoading(true);
    const token = localStorage.getItem('tennis_token');
    
    try {
      const response = await fetch(`${API_URL}/api/tennis/bookings/available?date=${date}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await readJson(response);
        setAvailableSlots(Array.isArray(data.slots) ? data.slots : []);
      } else {
        const error = await readJson(response);
        toast.error(error.detail || t('common.error'));
        setAvailableSlots([]);
      }
    } catch (error) {
      toast.error(t('common.error'));
      setAvailableSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  };

  const handleDateSelect = (date) => {
    const dateStr = toLocalKey(date);

    setSelectedDate(dateStr);
    setSelectedSlot(null);
    setDuration(1);
    setSlotsLoading(true);

    // Connect to WebSocket for real-time updates
    connectWebSocket(dateStr);

    // Also fetch via REST as fallback
    fetchAvailableSlots(dateStr);
  };

  const handleSelectSlot = (slot) => {
    if (!slot.is_available) return;
    setSelectedSlot(slot);
    setDuration(1);
  };

  const isSlotInCart = (court, dateStr, timeSlot) => pendingBookings.some(
    p => p.court_number === court && p.date === dateStr && p.time_slot === timeSlot
  );

  const handleAddToCart = () => {
    if (!selectedSlot || !selectedDate) return;
    if (pendingBookings.length >= 3) {
      toast.error(t('tennis.toastMax3'));
      return;
    }
    if (isSlotInCart(selectedSlot.court_number, selectedDate, selectedSlot.time_slot)) {
      toast.error(t('tennis.toastAlreadyInList'));
      return;
    }
    setPendingBookings([...pendingBookings, {
      court_number: selectedSlot.court_number,
      date: selectedDate,
      time_slot: selectedSlot.time_slot,
      duration_hours: duration,
    }]);
    setSelectedSlot(null);
    setDuration(1);
    toast.success(t('tennis.toastAdded'));
  };

  const handleRemoveFromCart = (idx) => {
    setPendingBookings(pendingBookings.filter((_, i) => i !== idx));
  };

  const handleConfirmAllBookings = async () => {
    if (pendingBookings.length === 0) return;
    setBookingLoading(true);
    const token = localStorage.getItem('tennis_token');

    try {
      // Use batch endpoint for combined confirmation email
      const response = await fetch(`${API_URL}/api/tennis/bookings/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ bookings: pendingBookings })
      });

      const data = await readJson(response);
      if (response.ok) {
        toast.success(t('tennis.toastConfirmed', { count: pendingBookings.length }));
        setPendingBookings([]);
        setSelectedSlot(null);
        fetchMyBookings(token);
        // Refresh slots
        if (selectedDate) fetchAvailableSlots(selectedDate);
      } else {
        toast.error(data.detail || t('common.error'));
      }
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setBookingLoading(false);
    }
  };

  const handleCancelSelection = () => {
    setSelectedSlot(null);
    setDuration(1);
  };

  const handleCancelBooking = async (bookingId) => {
    const token = localStorage.getItem('tennis_token');
    
    try {
      const response = await fetch(`${API_URL}/api/tennis/bookings/${bookingId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success(t('tennis.toastCancelled'));
        fetchMyBookings(token);
        if (selectedDate) {
          fetchAvailableSlots(selectedDate);
        }
      } else {
        const error = await readJson(response);
        toast.error(error.detail || t('common.error'));
      }
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('tennis_token');
    localStorage.removeItem('tennis_member');
    navigate('/tennis');
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();
    
    const calendarDays = [];
    
    // Empty slots before first day
    for (let i = 0; i < startDayOfWeek; i++) {
      calendarDays.push(null);
    }
    
    // Days of month
    for (let i = 1; i <= daysInMonth; i++) {
      calendarDays.push(new Date(year, month, i));
    }
    
    return calendarDays;
  };

  const isDateSelectable = (date) => {
    if (!date) return false;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Må være i dag eller senere
    if (date < today) return false;
    
    // Må være innen 14 dager (2 uker) frem i tid
    const maxBookingDate = new Date(today);
    maxBookingDate.setDate(maxBookingDate.getDate() + 14);
    
    if (date > maxBookingDate) return false;
    
    // Må være i sesongen (mai - oktober)
    const month = date.getMonth();
    if (month < 4 || month > 9) return false;
    
    return true;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-slate-900 dark:text-white">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div data-testid="tennis-booking-page" className="min-h-screen py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <Link to="/tennis" className="text-slate-500 hover:text-slate-900 dark:hover:text-white transition-colors">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-3xl font-bold text-slate-900 dark:text-white">{t('tennis.bookTennisCourt')}</h1>
                {selectedDate && (
                  <span className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full ${wsConnected ? 'bg-green-500/20 text-green-500' : 'bg-yellow-500/20 text-yellow-500'}`}>
                    {wsConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                    {wsConnected ? t('tennis.liveUpdates', 'Live') : t('tennis.connecting', 'Kobler til...')}
                  </span>
                )}
              </div>
              <p className="text-slate-500 dark:text-slate-400">{t('tennis.selectDateTime')}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
              <User className="w-5 h-5" />
              <span>{member?.first_name} {member?.last_name}</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              data-testid="my-bookings-link"
              onClick={() => navigate('/tennis/mine-bookinger')}
              className="border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              {t('tennis.myBookings')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <LogOut className="w-4 h-4 mr-2" />
              {t('tennis.logOut')}
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Calendar */}
          <div className="lg:col-span-2">
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6">
              {/* Month navigation */}
              <div className="flex items-center justify-between mb-6">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                  className="text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-slate-700"
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                  {months[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                </h2>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                  className="text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-slate-700"
                >
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>

              {/* Day headers */}
              <div className="grid grid-cols-7 gap-2 mb-2">
                {days.map((day) => (
                  <div key={day} className="text-center text-slate-400 dark:text-slate-500 text-sm font-medium py-2">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-2">
                {generateCalendarDays().map((date, index) => {
                  const isSelectable = isDateSelectable(date);
                  const isSelected = date && selectedDate === toLocalKey(date);
                  
                  return (
                    <button
                      key={index}
                      onClick={() => isSelectable && handleDateSelect(date)}
                      disabled={!isSelectable}
                      className={`
                        aspect-square rounded-lg flex items-center justify-center text-sm font-medium transition-all
                        ${!date ? 'invisible' : ''}
                        ${isSelected ? 'bg-green-500 text-white' : ''}
                        ${isSelectable && !isSelected ? 'bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-white hover:bg-slate-200 dark:hover:bg-slate-600 cursor-pointer' : ''}
                        ${!isSelectable && date ? 'bg-white dark:bg-slate-800 text-slate-400 dark:text-slate-600 cursor-not-allowed' : ''}
                      `}
                    >
                      {date?.getDate()}
                    </button>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="flex flex-wrap gap-4 mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-4 h-4 rounded bg-slate-100 dark:bg-slate-700"></div>
                  <span className="text-slate-500 dark:text-slate-400">{t('tennis.available')}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-4 h-4 rounded bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700"></div>
                  <span className="text-slate-500 dark:text-slate-400">{t('tennis.notAvailable')}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-4 h-4 rounded bg-green-500"></div>
                  <span className="text-slate-500 dark:text-slate-400">{t('tennis.selected')}</span>
                </div>
              </div>

              {/* Season info */}
              <div className="mt-4 bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                <p className="text-slate-600 dark:text-slate-300 text-sm">
                  <strong className="text-green-500">{t('tennis.season')}:</strong> {t('tennis.seasonInfo')}
                </p>
              </div>
            </div>

            {/* Time slots */}
            {selectedDate && (() => {
              // Trygg parsing - unngår timezone-shift som flytter datoen én dag
              const [yr, mo, dy] = selectedDate.split('-').map(Number);
              const localSelectedDate = new Date(yr, mo - 1, dy);
              return (
              <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 mt-6">
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-green-500" />
                  {t('tennis.availableTimes')} - {localSelectedDate.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' })}
                </h3>

                {slotsLoading ? (
                  <div className="text-center py-8 text-slate-500 dark:text-slate-400">{t('tennis.loadingTimes')}</div>
                ) : !availableSlots || availableSlots.length === 0 ? (
                  <div className="text-center py-8 text-slate-500 dark:text-slate-400">{t('tennis.noAvailableTimes')}</div>
                ) : (
                  (() => {
                    // Hvis brukeren valgte dagens dato, blokker tider som allerede har passert
                    const _now = new Date();
                    const todayStr = `${_now.getFullYear()}-${String(_now.getMonth() + 1).padStart(2, '0')}-${String(_now.getDate()).padStart(2, '0')}`;
                    const isToday = selectedDate === todayStr;
                    const currentHour = _now.getHours();
                    const isPastSlot = (slot) => isToday && parseInt(slot.time_slot.split(':')[0], 10) <= currentHour;

                    const renderSlots = (courtNum) => (availableSlots || [])
                      .filter(s => s.court_number === courtNum)
                      .map((slot) => {
                        const past = isPastSlot(slot);
                        const isSelected = selectedSlot && selectedSlot.court_number === courtNum && selectedSlot.time_slot === slot.time_slot;
                        const inCart = isSlotInCart(courtNum, selectedDate, slot.time_slot);
                        const bookable = slot.is_available && !past && !inCart;

                        let className = 'py-3 px-2 rounded-lg text-sm font-medium transition-all border-2 ';
                        if (isSelected) {
                          className += 'bg-green-500 text-white border-green-600 shadow-md scale-105';
                        } else if (inCart) {
                          className += 'bg-blue-500/30 text-blue-700 dark:text-blue-300 border-blue-500/50 cursor-not-allowed';
                        } else if (bookable) {
                          className += 'bg-green-500/20 text-green-600 border-green-500/30 hover:bg-green-500 hover:text-white hover:border-green-500';
                        } else {
                          className += 'bg-slate-100 dark:bg-slate-700 text-slate-400 dark:text-slate-500 border-transparent cursor-not-allowed';
                        }

                        return (
                          <button
                            key={`${courtNum}-${slot.time_slot}`}
                            onClick={() => bookable && handleSelectSlot(slot)}
                            disabled={!bookable || bookingLoading}
                            data-testid={`slot-${courtNum}-${slot.time_slot}`}
                            className={className}
                            title={
                              past ? t('tennis.slotPast') :
                              inCart ? t('tennis.toastAlreadyInList') :
                              !slot.is_available ? t('tennis.slotBooked') : ''
                            }
                          >
                            {slot.time_slot}
                          </button>
                        );
                      });

                    return (
                      <div className="space-y-6">
                        {/* Court 1 */}
                        <div>
                          <h4 className="text-lg font-medium text-slate-900 dark:text-white mb-3">{t('tennis.court')} 1</h4>
                          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                            {renderSlots(1)}
                          </div>
                        </div>

                        {/* Court 2 */}
                        <div>
                          <h4 className="text-lg font-medium text-slate-900 dark:text-white mb-3">{t('tennis.court')} 2</h4>
                          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                            {renderSlots(2)}
                          </div>
                        </div>

                        {/* Confirmation card - vises når en tid er valgt */}
                        {selectedSlot && (
                          <div data-testid="booking-confirmation" className="mt-6 bg-green-500/10 border-2 border-green-500/40 rounded-xl p-5 animate-fade-in-up">
                            <h4 className="font-bold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                              <Calendar className="w-5 h-5 text-green-500" />
                              {t('tennis.confirmYourBooking')}
                            </h4>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4 text-sm">
                              <div className="bg-white dark:bg-slate-800 rounded-lg p-3 border border-slate-200 dark:border-slate-700">
                                <p className="text-xs text-slate-500 dark:text-slate-400">{t('tennis.court')}</p>
                                <p className="font-bold text-slate-900 dark:text-white">{t('tennis.court')} {selectedSlot.court_number}</p>
                              </div>
                              <div className="bg-white dark:bg-slate-800 rounded-lg p-3 border border-slate-200 dark:border-slate-700">
                                <p className="text-xs text-slate-500 dark:text-slate-400">{t('tennis.dateAndTime')}</p>
                                <p className="font-bold text-slate-900 dark:text-white">
                                  {localSelectedDate.toLocaleDateString(locale, { day: 'numeric', month: 'long' })} {t('tennis.at')} {selectedSlot.time_slot}
                                </p>
                              </div>
                              <div className="bg-white dark:bg-slate-800 rounded-lg p-3 border border-slate-200 dark:border-slate-700">
                                <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">{t('tennis.duration')}</p>
                                <div className="flex gap-2">
                                  <button
                                    type="button"
                                    onClick={() => setDuration(1)}
                                    data-testid="duration-1h"
                                    className={`px-3 py-1 rounded-md text-xs font-medium border ${duration === 1 ? 'bg-green-500 text-white border-green-500' : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600'}`}
                                  >1 {t('tennis.hour')}</button>
                                  <button
                                    type="button"
                                    onClick={() => setDuration(2)}
                                    data-testid="duration-2h"
                                    className={`px-3 py-1 rounded-md text-xs font-medium border ${duration === 2 ? 'bg-green-500 text-white border-green-500' : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600'}`}
                                  >2 {t('tennis.hours')}</button>
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-2">
                              <button
                                onClick={handleAddToCart}
                                disabled={bookingLoading || pendingBookings.length >= 3}
                                data-testid="add-to-cart-btn"
                                className="flex-1 bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded-lg transition-colors disabled:opacity-50"
                              >
                                {pendingBookings.length >= 3
                                  ? t('tennis.maxReached')
                                  : t('tennis.addToList', { duration, unit: t(duration === 1 ? 'tennis.hour' : 'tennis.hours') })}
                              </button>
                              <button
                                onClick={handleCancelSelection}
                                disabled={bookingLoading}
                                data-testid="cancel-selection-btn"
                                className="px-4 py-3 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                              >
                                {t('tennis.cancel')}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()
                )}
              </div>
              );
            })()}
          </div>

          {/* My Bookings */}
          <div>
            {/* Pending bookings cart */}
            {pendingBookings.length > 0 && (
              <div data-testid="pending-cart" className="bg-green-500/10 border-2 border-green-500/40 rounded-2xl p-5 mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-slate-900 dark:text-white flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-green-500" />
                    {t('tennis.readyToConfirm', { count: pendingBookings.length })}
                  </h3>
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-300 mb-3">
                  {t('tennis.pickMoreOrConfirm')}
                </p>
                <div className="space-y-2 mb-4">
                  {pendingBookings.map((b, i) => {
                    const [yr, mo, dy] = b.date.split('-').map(Number);
                    const d = new Date(yr, mo - 1, dy);
                    return (
                      <div key={i} className="bg-white dark:bg-slate-800 rounded-lg p-3 border border-slate-200 dark:border-slate-700 flex items-center justify-between gap-2">
                        <div className="text-sm">
                          <p className="font-bold text-slate-900 dark:text-white">{t('tennis.court')} {b.court_number} – {b.time_slot}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            {d.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' })}
                            {' · '}{b.duration_hours} {t(b.duration_hours === 1 ? 'tennis.hour' : 'tennis.hours')}
                          </p>
                        </div>
                        <button
                          onClick={() => handleRemoveFromCart(i)}
                          data-testid={`remove-cart-${i}`}
                          aria-label="Fjern fra liste"
                          className="text-red-500 hover:text-red-700 transition-colors p-1"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
                <button
                  onClick={handleConfirmAllBookings}
                  disabled={bookingLoading}
                  data-testid="confirm-all-bookings-btn"
                  className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded-lg transition-colors disabled:opacity-50"
                >
                  {bookingLoading
                    ? t('tennis.confirming')
                    : pendingBookings.length === 1 ? t('tennis.confirmOne') : t('tennis.confirmAll', { count: pendingBookings.length })}
                </button>
              </div>
            )}

            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 sticky top-24">
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-green-500" />
                {t('tennis.myBookings')}
              </h3>

              {myBookings.filter(b => b.status === 'confirmed').length === 0 ? (
                <p className="text-slate-500 dark:text-slate-400 text-sm">{t('tennis.noBookings')}</p>
              ) : (
                <div className="space-y-3">
                  {myBookings
                    .filter(b => b.status === 'confirmed')
                    .sort((a, b) => new Date(a.date) - new Date(b.date))
                    .map((booking) => (
                      <div 
                        key={booking.id}
                        className="bg-slate-100 dark:bg-slate-700 rounded-lg p-4"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="text-slate-900 dark:text-white font-medium">{t('tennis.court')} {booking.court_number}</p>
                            <p className="text-green-500 text-sm">{booking.time_slot}</p>
                          </div>
                          <button
                            onClick={() => handleCancelBooking(booking.id)}
                            className="text-red-400 hover:text-red-300 p-1"
                            title={t('tennis.cancelBooking')}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <p className="text-slate-500 dark:text-slate-400 text-sm">
                          {new Date(booking.date).toLocaleDateString(locale, { 
                            weekday: 'long', 
                            day: 'numeric', 
                            month: 'long' 
                          })}
                        </p>
                      </div>
                    ))}
                </div>
              )}

              <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                  <p className="text-yellow-500 text-xs flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    {t('tennis.cancelNote')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TennisBooking;
